import { createSelectors } from "@/redux/helpers/selector";
import { ENTER_SQM_DIMENSIONS_IN_MM } from "@/utils/measurement/constant";

import { MODULE } from "./constants";

export const [getCurrentCompany] = createSelectors(MODULE, ["currentCompany"]);

export const getCurrencySymbol = (state) => getCurrentCompany(state)?.currencySymbol ?? "$";

export const isFeatureFlagEnabledWeb = (state, code) => {
  const flags = getCurrentCompany(state)?.featureFlags ?? [];
  return flags.find((flag) => flag.code === code)?.isEnabledWeb ?? false;
};

/**
 * @param {object} state redux store state
 * @param {string} params.settingsType primary object key to find settings
 * @param {string} params.subType secondary type to find settings
 * @param {string} params.settingsKey key to find specific record of settings
 * @param {string=} params.specificKey optional key to get the specific details in the record
 * @returns {object | string | boolen | null}
 */
export const getCompanySettings = (
  state,
  { settingsType, subType, settingsKey, specificKey = "" }
) => {
  const settings = getCurrentCompany(state)?.settings ?? null;

  if (!settings) {
    return null;
  }

  if (!settings[settingsType] || !settingsKey) {
    return null;
  }

  const { [settingsType]: companySettingsType } = settings;

  if (companySettingsType?.[subType]) {
    const specificSettings = companySettingsType[subType].find(({ key }) => key === settingsKey);

    if (typeof specificSettings === "object" && specificKey in specificSettings) {
      return specificSettings[specificKey];
    }

    return specificSettings || null;
  }

  return null;
};

export const getInvoiceSettings = (state) => {
  const company = getCurrentCompany(state);

  return company?.settings?.invoiceSettings ?? null;
};

export const getOrdersSettings = (state, { key }) => {
  const company = getCurrentCompany(state);
  const orders = company?.settings?.ordersSettings?.orders ?? [];

  if (orders.length === 0) {
    return null;
  }

  const selectedSetting = orders.find((p) => p.key === key);

  if (selectedSetting) {
    return selectedSetting;
  }
};

export const getPlannerSettings = (state, { key }) => {
  const selectedSetting = getCurrentCompany(state).settings.plannerSettings.find(
    (p) => p.key === key
  );

  if (selectedSetting) {
    return selectedSetting;
  }

  return null;
};

export const getTimeTrackingSettings = (state, { key }) => {
  const selectedSetting = getCurrentCompany(state).settings.timeTrackingSettings.settings.find(
    (p) => p.key === key
  );

  if (selectedSetting) {
    return selectedSetting;
  }

  return null;
};

export const getPdfSettings = (state, { key }) => {
  const pdfSettings = getCurrentCompany(state)?.settings?.pdfSettings ?? [];
  return pdfSettings.find((p) => p.key === key) ?? null;
};

// Minimum length for Lineal Metres
export const getMinLengthLinealMeters = (state) => {
  const company = getCurrentCompany(state);
  let minLength = 0;

  if (company) {
    const productOptions = company?.settings?.ordersSettings?.productOptions ?? [];
    const settingsLength = productOptions.find((setting) => setting.key === "AP_MIN_LENGTH_MM");

    if (settingsLength?.isSelected) {
      minLength = parseInt(settingsLength.value, 10);
    }
  }

  return minLength;
};

// Minimum length for Lineal Feet
export const getMinLengthLinealFeet = (state) => {
  const company = getCurrentCompany(state);
  if (company) {
    const productOptions = company?.settings?.ordersSettings?.productOptions ?? [];
    const settingsLength = productOptions.find((setting) => setting.key === "AP_MIN_LENGTH_FT");

    if (settingsLength?.isSelected) {
      return parseFloat(settingsLength.value);
    }
  }
  return 0;
};

export const getCompanyTaxRate = (state) => {
  const company = getCurrentCompany(state);
  return company?.taxRate;
};

// Label shown next to tax amounts (e.g. "Tax", "VAT", "GST"); "Tax" matches the backend default
export const getTaxLabel = (state) => getCurrentCompany(state)?.taxLabel || "Tax";

// Get selected sort length option
export const getSortLengthsSetting = (state) => {
  const company = getCurrentCompany(state);

  if (company) {
    const orderSettings = company?.settings?.ordersSettings?.orders ?? [];

    const settingsSortLength = orderSettings.find((setting) => setting.key === "AP_SORT_LENGTHS");

    if (settingsSortLength) {
      return settingsSortLength.selected ?? null;
    }

    return null;
  }

  return null;
};

// FAC-5925: when this company option is on, a new square-metre dimension row
// starts in millimetres. If there is no company loaded yet, or the option is
// missing, new rows start in metres - the behaviour every company had before.
export const getEnterSquareMetreDimensionsInMmSetting = (state) => {
  const orderOptions = getCurrentCompany(state)?.settings?.ordersSettings?.orderOptions ?? [];
  const setting = orderOptions.find((option) => option.key === ENTER_SQM_DIMENSIONS_IN_MM);

  return setting?.isSelected === true;
};

const CUSTOMER_DATE_FORMAT_KEY = "CUSTOMER_DATE_FORMAT";
const DEFAULT_DATE_FORMAT = "DD/MM/YYYY";
export const getCustomerDateFormatSetting = (state) => {
  const company = getCurrentCompany(state);
  const customerSettings = company?.settings?.customerSettings || [];
  const dateFormatSetting = customerSettings.find(
    (setting) => setting.key === CUSTOMER_DATE_FORMAT_KEY
  );
  return dateFormatSetting?.value || company?.dateFormat || DEFAULT_DATE_FORMAT;
};

export const getAccountingSyncModule = (state, module) => {
  const company = getCurrentCompany(state);
  const moduleObject = company.accountingSyncModules.find((m) => m.module === module);

  return moduleObject;
};
